import cn from "classnames";
import { useBoolean, useOnClickOutside } from "hooks";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ClipLoader } from "react-spinners";

import { ConversationContact } from "../../aiConversations.types";
import { WhatsAppConversationStatus } from "../../constants/enums";
import { ConversationItem } from "../ConversationItem";

import styles from "./ConversationsList.module.scss";

export type ConversationsStatusFilter = WhatsAppConversationStatus | "all";

interface Props {
  conversations: ConversationContact[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isLoading?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  status: ConversationsStatusFilter;
  onStatusChange: (next: ConversationsStatusFilter) => void;
  hideChannel?: boolean;
}

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M14 14L11.1 11.1M12.6667 7.33333C12.6667 10.2789 10.2789 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.2789 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.2789 2 12.6667 4.38781 12.6667 7.33333Z"
      stroke="#7E7E8B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M3 4.5L6 7.5L9 4.5"
      stroke="#7E7E8B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const STATUS_OPTIONS: { value: ConversationsStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: WhatsAppConversationStatus.Ai, label: "AI" },
  { value: WhatsAppConversationStatus.Human, label: "Human" },
  { value: WhatsAppConversationStatus.Closed, label: "Closed" }
];

const LOAD_MORE_THRESHOLD_PX = 120;

const ConversationsList = ({
  conversations,
  selectedId,
  onSelect,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isLoading,
  search,
  onSearchChange,
  searchPlaceholder,
  status,
  onStatusChange,
  hideChannel
}: Props) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement | null>(null);
  const filterRef = useRef<HTMLDivElement | null>(null);

  const {
    value: filterOpen,
    toggle: toggleFilter,
    setFalse: closeFilter
  } = useBoolean(false);

  useOnClickOutside(filterRef, closeFilter);

  useEffect(() => {
    const node = listRef.current;
    if (!node || !onLoadMore) return;

    const handleScroll = () => {
      if (!hasMore || isLoadingMore) return;
      const distanceFromBottom =
        node.scrollHeight - node.scrollTop - node.clientHeight;
      if (distanceFromBottom <= LOAD_MORE_THRESHOLD_PX) {
        onLoadMore();
      }
    };

    node.addEventListener("scroll", handleScroll);
    return () => node.removeEventListener("scroll", handleScroll);
  }, [onLoadMore, hasMore, isLoadingMore]);

  const currentStatusLabel =
    STATUS_OPTIONS.find((o) => o.value === status)?.label ?? "All";

  const showEmptyState =
    !isLoading && !isLoadingMore && conversations.length === 0;

  return (
    <div className={styles.panel}>
      <div className={styles.controls}>
        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>
            <SearchIcon />
          </span>
          <input
            className={styles.searchInput}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t(searchPlaceholder ?? "Search by phone...")}
          />
        </div>

        <div className={styles.filterWrapper} ref={filterRef}>
          <button
            type="button"
            className={styles.filterButton}
            onClick={toggleFilter}
          >
            <span>{t(currentStatusLabel)}</span>
            <ChevronDown />
          </button>

          {filterOpen ? (
            <div className={styles.filterDropdown}>
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={cn(styles.filterOption, {
                    [styles.filterOptionActive]: option.value === status
                  })}
                  onClick={() => {
                    onStatusChange(option.value);
                    closeFilter();
                  }}
                >
                  {t(option.label)}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className={styles.list} ref={listRef}>
        {isLoading ? (
          <div className={styles.loaderCentered}>
            <ClipLoader size={28} color="#fb3973" />
          </div>
        ) : (
          <>
            {conversations.map((conv) => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                isActive={conv.id === selectedId}
                hideChannel={hideChannel}
                onClick={() => onSelect(conv.id)}
              />
            ))}
            {showEmptyState ? (
              <div className={styles.emptyState}>
                {t("No conversations found")}
              </div>
            ) : null}
            {isLoadingMore ? (
              <div className={styles.loadingIndicator}>
                <ClipLoader size={18} color="#fb3973" />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default ConversationsList;
